import { DashboardLoanTransactionDetailModelDto } from '@treasury/api/channel';
import { formatUsdCurrency } from '@treasury/utils';
import { format } from 'date-fns';
import { DomainEntity } from '../../shared';
import { emptyAdditionalAccountDetails } from '../../company/company-account.entity';

const defaultLoanAccountDetail: DashboardLoanTransactionDetailModelDto = {
    currentBalance: 0,
    originalLoanTerm: '',
    accruedInterest: 0,
    lastPaymentDate: '',
    nextPaymentDate: '',
    paymentFrequency: '',
    availableCredit: 0,
    lineOfCreditLimit: 0,
    ...emptyAdditionalAccountDetails,
};

export class LoanAccountDetail extends DomainEntity<DashboardLoanTransactionDetailModelDto> {
    constructor(public source = defaultLoanAccountDetail) {
        super(source);
    }

    public createDefault(): this {
        return new LoanAccountDetail(defaultLoanAccountDetail) as this;
    }

    public get originalLoanAmount() {
        return formatUsdCurrency(this.source.originalLoanAmount);
    }

    public get currentBalance() {
        return formatUsdCurrency(this.source.currentBalance);
    }

    public get originalLoanTerm() {
        return this.source.originalLoanTerm;
    }

    public get interestRate() {
        return new Intl.NumberFormat('default', {
            style: 'percent',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(this.source.interestRate);
    }

    public get accruedInterest() {
        return formatUsdCurrency(this.source.accruedInterest);
    }

    public get totalAmountDue() {
        return formatUsdCurrency(this.source.totalAmountDue);
    }

    public get lastPaymentDate() {
        return asFormattedDateOrEmptyString(this.source.lastPaymentDate);
    }

    public get nextPaymentDate() {
        return asFormattedDateOrEmptyString(this.source.nextPaymentDate);
    }

    public get paymentFrequency() {
        return this.source.paymentFrequency;
    }

    public get remainingPayments() {
        return this.source.remainingPayments;
    }

    public get availableCredit() {
        return formatUsdCurrency(this.source.availableCredit || 0);
    }

    public get lineOfCreditLimit() {
        return formatUsdCurrency(this.source.lineOfCreditLimit || 0);
    }

    public get originalLoanDate() {
        return asFormattedDateOrEmptyString(this.source.originalLoanDate);
    }

    public get originalBalance() {
        return formatUsdCurrency(this.source.originalBalance);
    }

    public get maturityDate() {
        return asFormattedDateOrEmptyString(this.source.maturityDate);
    }

    public get currentPayoff() {
        return formatUsdCurrency(this.source.currentPayoff);
    }

    public get payoffGoodThru() {
        return asFormattedDateOrEmptyString(this.source.payoffGoodThru);
    }

    public get interestPaidYtd() {
        return formatUsdCurrency(this.source.interestPaidYtd);
    }

    public get paymentDueDate() {
        return asFormattedDateOrEmptyString(this.source.paymentDueDate);
    }

    public get paymentAmountDue() {
        return formatUsdCurrency(this.source.paymentAmountDue);
    }

    public get pastAmountDue() {
        return formatUsdCurrency(this.source.pastAmountDue);
    }

    public get paymentTotalAmount() {
        return formatUsdCurrency(this.source.paymentTotalAmount);
    }

    public get billedPrincipal() {
        return formatUsdCurrency(this.source.billedPrincipal);
    }

    public get billedInterest() {
        return formatUsdCurrency(this.source.billedInterest);
    }

    public get billedEscrow() {
        return formatUsdCurrency(this.source.billedEscrow);
    }

    public get billedLateCharge() {
        return formatUsdCurrency(this.source.billedLateCharge);
    }

    public get billedOtherCharge() {
        return formatUsdCurrency(this.source.billedOtherCharge);
    }
}

function asFormattedDateOrEmptyString(date: Date | string | undefined) {
    if (!date) {
        return '';
    }
    return format(date, 'MM/dd/yyyy');
}
